import * as THREE from 'three';
import type { BallState } from './BallState';

export interface CupCaptureConfig {
  readonly cupRadiusMeters?: number;
  readonly captureRadiusMeters?: number;
  readonly captureSpeedMetersPerSecond?: number;
  readonly lipSpeedMetersPerSecond?: number;
}

export type CupCaptureResult = 'none' | 'capture' | 'too-fast' | 'lip-out';

/**
 * Forgiving putting capture tuned around a real cup diameter plus a small lip.
 * It captures slow/moderate putts that reach the edge or cross close to the cup,
 * while preserving long misses when the ball arrives too hot.
 */
export class CupCapture {
  readonly cupRadiusMeters: number;
  readonly captureRadiusMeters: number;
  readonly captureSpeedMetersPerSecond: number;
  readonly lipSpeedMetersPerSecond: number;

  constructor(config: CupCaptureConfig = {}) {
    this.cupRadiusMeters = config.cupRadiusMeters ?? 0.054;
    this.captureRadiusMeters = config.captureRadiusMeters ?? 0.08;
    this.captureSpeedMetersPerSecond = config.captureSpeedMetersPerSecond ?? 2.05;
    this.lipSpeedMetersPerSecond = config.lipSpeedMetersPerSecond ?? 2.65;
  }

  evaluate(
    previousPosition: THREE.Vector3,
    state: BallState,
    cupPosition: THREE.Vector3,
  ): CupCaptureResult {
    if (state.isResting || state.phase !== 'rolling') return 'none';

    const speed = state.velocity.length();
    const closestDistance = this.closestFlatDistance(previousPosition, state.position, cupPosition);
    const currentDistance = state.position.clone().sub(cupPosition).setY(0).length();
    const reachedCup =
      closestDistance <= this.captureRadiusMeters || currentDistance <= this.captureRadiusMeters;
    if (!reachedCup) return 'none';

    const offsetRatio = THREE.MathUtils.clamp(closestDistance / this.captureRadiusMeters, 0, 1);
    const centeredBonus = 1 - offsetRatio;
    const dynamicCaptureSpeed = this.captureSpeedMetersPerSecond * (0.86 + centeredBonus * 0.58);
    if (speed <= dynamicCaptureSpeed) return 'capture';
    if (closestDistance <= this.cupRadiusMeters && speed <= this.lipSpeedMetersPerSecond)
      return 'capture';
    if (
      speed <= this.lipSpeedMetersPerSecond &&
      closestDistance <= this.captureRadiusMeters * 1.15
    ) {
      const away = state.position.clone().sub(cupPosition).setY(0).normalize();
      state.velocity.addScaledVector(away, 0.35).multiplyScalar(0.72);
      return 'lip-out';
    }
    return 'too-fast';
  }

  private closestFlatDistance(
    start: THREE.Vector3,
    end: THREE.Vector3,
    point: THREE.Vector3,
  ): number {
    const a = new THREE.Vector2(start.x, start.z);
    const b = new THREE.Vector2(end.x, end.z);
    const p = new THREE.Vector2(point.x, point.z);
    const segment = b.clone().sub(a);
    const lengthSq = segment.lengthSq();
    if (lengthSq < 0.000001) return p.distanceTo(b);
    const t = THREE.MathUtils.clamp(p.clone().sub(a).dot(segment) / lengthSq, 0, 1);
    return p.distanceTo(a.add(segment.multiplyScalar(t)));
  }
}
